import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface AnimatedStatCardProps {
  title: string;
  value: number;
  icon: LucideIcon;
  gradient: string;
  iconBg: string;
  trend?: number;
  onClick?: () => void;
}

export const AnimatedStatCard = ({
  title,
  value,
  icon: Icon,
  gradient,
  iconBg,
  trend,
  onClick,
}: AnimatedStatCardProps) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1200;
    const increment = value / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <Card
      className={cn(
        "group relative overflow-hidden p-5 border border-border/40 bg-card/40 backdrop-blur-xl",
        "transition-all duration-500 hover:border-border hover:-translate-y-0.5 hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.25)]",
        "animate-fade-in",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      {/* Subtle gradient wash */}
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-br opacity-60 transition-opacity duration-500 group-hover:opacity-100",
          gradient
        )}
      />
      {/* Top shine */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-foreground/10 to-transparent" />

      <div className="relative">
        <div className="flex items-start justify-between mb-6">
          <div
            className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-black/5 ring-1 ring-white/10",
              iconBg
            )}
          >
            <Icon className="w-5 h-5 text-white" />
          </div>
          {trend !== undefined && (
            <div
              className={cn(
                "text-[10px] font-semibold px-2 py-1 rounded-full backdrop-blur",
                trend >= 0
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                  : "bg-red-500/15 text-red-600 dark:text-red-400"
              )}
            >
              {trend >= 0 ? "↑ " : "↓ "}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-[0.15em]">
          {title}
        </div>
        <div className="text-3xl font-semibold tracking-tight mt-1.5 tabular-nums">
          {displayValue}
        </div>
      </div>
    </Card>
  );
};
